import { createApi } from '@reduxjs/toolkit/query/react';
import { getHeaders, CheckAuth } from 'auth/auth';
import { baseQueryWithReauth } from '../baseQuery';

const isUuidLike = (value) => {
  if (!value) return false;
  return /^[0-9a-fA-F-]{36}$/.test(String(value));
};

const resolvePortalPrefix = () => {
  const cachedUser = CheckAuth() || {};
  const role = String(cachedUser.role || cachedUser.originalRole || '').toLowerCase();
  const userId = cachedUser.user_id;

  if (role === 'consumer' || isUuidLike(userId)) {
    return '/b2c';
  }

  return '/b2b';
};

const withPortalPrefix = (path = '') => {
  const prefix = resolvePortalPrefix();
  if (!path) return prefix;
  if (path.startsWith('/b2b') || path.startsWith('/b2c')) return path;
  if (path.startsWith('/')) return `${prefix}${path}`;
  return `${prefix}/${path}`;
};

export const applicationAPI = createApi({
  reducerPath: 'profile',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'getApplication',
    'getPersonalDetails',
    'getLoanDetails',
    'getEmploymentDetails',
    'getAssetDetails',
    'getLiabilitiesDetails',
    'getRealEstateDetails',
    'getCoBorrowerPersonalDetails',
    'getCoBorrowerEmploymentDetails',
    'getCoBorrowerAssetDetails',
    'getCoBorrowerLiabilitiesDetails',
    'getCoBorrowerRealEstateDetails',
    'getDeclarationsDetails',
    'getDemographicsDetails',
  ],
  endpoints: (builder) => ({
    // ── Legacy Heavy GET (kept for backward compatibility) ─────────
    getBorrowerProfile: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `b2c/loan/dashboard/borrower_profile/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getApplication'],
      keepUnusedDataFor: 300,
    }),

    // ── Per-Section Lightweight GET Endpoints ──────────────────────
    getPersonalDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/dashboard/personal_details/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getPersonalDetails'],
      keepUnusedDataFor: 300,
    }),
    getLoanDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/dashboard/loan_details/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getLoanDetails'],
      keepUnusedDataFor: 300,
    }),
    getEmploymentDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/dashboard/employment_and_income/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getEmploymentDetails'],
      keepUnusedDataFor: 300,
    }),
    getAssetDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/dashboard/assets/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getAssetDetails'],
      keepUnusedDataFor: 300,
    }),
    getLiabilitiesDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/dashboard/liabilities/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getLiabilitiesDetails'],
      keepUnusedDataFor: 300,
    }),
    getRealEstateDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/dashboard/reos/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getRealEstateDetails'],
      keepUnusedDataFor: 300,
    }),

    getDeclarationsDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/dashboard/declarations/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getDeclarationsDetails'],
      keepUnusedDataFor: 300,
    }),

    getDemographicsDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/dashboard/demographics/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getDemographicsDetails'],
      keepUnusedDataFor: 300,
    }),

    // ── Co-Borrower Section GET Endpoints ─────────────────────────
    getCoBorrowerPersonalDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/co_borrower/personal_details/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getCoBorrowerPersonalDetails'],
      keepUnusedDataFor: 300,
    }),
    getCoBorrowerEmploymentDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/co_borrower/employment_and_income/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getCoBorrowerEmploymentDetails'],
      keepUnusedDataFor: 300,
    }),
    getCoBorrowerAssetDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/co_borrower/assets/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getCoBorrowerAssetDetails'],
      keepUnusedDataFor: 300,
    }),
    getCoBorrowerLiabilitiesDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/co_borrower/liabilities/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getCoBorrowerLiabilitiesDetails'],
      keepUnusedDataFor: 300,
    }),
    getCoBorrowerRealEstateDetails: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/co_borrower/reos/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getCoBorrowerRealEstateDetails'],
      keepUnusedDataFor: 300,
    }),

    // ── Primary Borrower Write Mutations ──────────────────────────
    createPersonalDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/personal_details`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getPersonalDetails', 'getApplication'],
    }),
    updatePersonalDetails: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `b2c/loan/dashboard/personal_details/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getPersonalDetails', 'getApplication'],
    }),
    createLoanDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/loan`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getLoanDetails', 'getApplication'],
    }),
    updateLoanDetails: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `b2c/loan/dashboard/loan/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getLoanDetails', 'getApplication'],
    }),
    createEmploymentDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/employment_and_income`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getEmploymentDetails', 'getApplication'],
    }),
    updateEmploymentDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/employment_and_income`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getEmploymentDetails', 'getApplication'],
    }),
    createAssetDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/assets`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getAssetDetails', 'getApplication'],
    }),
    updateAssetDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/assets`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getAssetDetails', 'getApplication'],
    }),
    deleteAssetDetails: builder.mutation({
      query: ({ assetId, userId, applicationId }) => {
        const params = {};
        if (userId) params.user_id = userId;
        if (applicationId) params.applicationId = applicationId;
        return {
          url: `b2c/loan/dashboard/assets/${assetId}`,
          method: 'DELETE',
          params: Object.keys(params).length ? params : undefined,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getAssetDetails', 'getApplication'],
    }),
    createLiabilitiesDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/liabilities`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getLiabilitiesDetails', 'getApplication'],
    }),
    updateLiabilitiesDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/liabilities`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getLiabilitiesDetails', 'getApplication'],
    }),
    createRealEstateDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/reos`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getRealEstateDetails', 'getApplication'],
    }),
    updateRealEstateDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/reos`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getRealEstateDetails', 'getApplication'],
    }),
    createDeclarationDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/declaration`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getDeclarationsDetails', 'getApplication'],
    }),
    updateDeclarationDetails: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `b2c/loan/dashboard/declaration/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getDeclarationsDetails', 'getApplication'],
    }),
    createDemographicsDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/demographics`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getDemographicsDetails', 'getApplication'],
    }),
    updateDemographicsDetails: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `b2c/loan/dashboard/demographics/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getDemographicsDetails', 'getApplication'],
    }),

    // REMOVED: getApplication — consolidated into getBorrowerProfile (same URL, shared cache)



    getOwnerShip: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/ownership'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getLoanPurpose: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/loan_purpose'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getLoanType: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/loan_type'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getOccupancy: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/occupancy'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getLoanProgram: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/loan_program'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getEmploymentType: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/employment_type'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getPropertyType: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/property_type'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getAccountType: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/account_type'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getLiabilityType: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/liability_type'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getRefinancePurpose: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/refinance_purpose'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getMartialStatus: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/marital_status'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getCitizenshipStatus: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/citizenship_status'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getUserRace: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/race'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getAssetType: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/asset_type'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getPropertyRight: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/property_right'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getMannerHeld: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/manner_held'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getLoanTermYears: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/loan_term'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getIncomeType: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/income_type'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getApplicationStatus: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/application_status'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getEthnicity: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/ethnicity'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    // get state
    getStates: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/states'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    getCountiesByState: builder.query({
      query: (stateCode) => {
        return {
          url: withPortalPrefix(`/loan/application_dropdowns/counties/${stateCode}`),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    // Bundle endpoint: fetches all dropdown categories in one request (B2C-specific)
    getDropdownBundle: builder.query({
      query: () => ({
        url: 'b2c/loan/application_dropdowns/bundle',
        method: 'GET',
        headers: getHeaders(),
      }),
      keepUnusedDataFor: 300, // 5-minute cache
    }),

    // Bundle endpoint: role-aware (routes to /b2b/ or /b2c/ based on user role)
    getLoanInfoDropdownBundle: builder.query({
      query: () => ({
        url: withPortalPrefix('/loan/application_dropdowns/bundle'),
        method: 'GET',
        headers: getHeaders(),
      }),
      keepUnusedDataFor: 300, // 5-minute cache
    }),
    // REMOVED: getDynamicApplication — consolidated into getBorrowerProfile (same URL, shared cache)


    // ── Spouse / Co-Borrower Personal Details (B2C) ───────────────
    createSpousePersonalDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/co_borrower/personal_details`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getPersonalDetails', 'getCoBorrowerPersonalDetails', 'getApplication'],
    }),
    updateSpousePersonalDetails: builder.mutation({
      query: ({ applicationId, data }) => ({
        url: `b2c/loan/co_borrower/personal_details/${applicationId}`,
        method: 'PUT',
        body: data,
        headers: getHeaders(),
      }),
      invalidatesTags: ['getPersonalDetails', 'getCoBorrowerPersonalDetails', 'getApplication'],
    }),
    addSpouseCoBorrower: builder.mutation({
      query: ({ applicationId, firstName, lastName }) => {
        return {
          url: `b2c/loan/co_borrower/add-spouse`,
          method: 'POST',
          body: {
            applicationId,
            firstName,
            lastName,
          },
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getPersonalDetails', 'getCoBorrowerPersonalDetails', 'getApplication'],
    }),
    inviteCoBorrower: builder.mutation({
      query: ({
        applicationId,
        firstName,
        lastName,
        email,
        phoneNumber,
        relationship,
      }) => {
        return {
          url: `b2c/loan/co_borrower/invite`,
          method: 'POST',
          body: {
            applicationId,
            firstName,
            lastName,
            email,
            phoneNumber,
            relationship,
          },
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getPersonalDetails', 'getCoBorrowerPersonalDetails', 'getApplication'],
    }),
    // Get all co-borrowers for a loan application (returns only accepted co-borrowers)
    getLoanCoborrowers: builder.query({
      query: ({ loanId }) => {
        return {
          url: `b2c/loan/${loanId}/co_borrowers`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: (result, error, { loanId }) => [
        { type: 'getApplication', id: loanId },
      ],
      keepUnusedDataFor: 300, // 5 minutes cache
    }),
    // Remove a co-borrower from a loan application
    removeCoborrower: builder.mutation({
      query: ({ coborrower_id, loan_id }) => {
        return {
          url: `b2c/loan/co_borrower/${coborrower_id}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: (result, error, { loan_id }) => [
        'getApplication',
        'getPersonalDetails',
        'getCoBorrowerPersonalDetails',
        { type: 'getApplication', id: loan_id },
      ],
    }),

    // ── Dynamic Employment Mutations ──────────────────────────────
    createDynamicEmploymentDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/employment_and_income`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getEmploymentDetails', 'getApplication'],
    }),
    updateDynamicEmploymentDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/employment_and_income`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getEmploymentDetails', 'getApplication'],
    }),
    deleteDynamicEmploymentDetails: builder.mutation({
      query: ({ employmentIncomeId, userId, applicationId }) => {
        const params = {};
        if (userId) params.user_id = userId;
        if (applicationId) params.applicationId = applicationId;
        return {
          url: `b2c/loan/dashboard/employment_and_income/${employmentIncomeId}`,
          method: 'DELETE',
          params: Object.keys(params).length ? params : undefined,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getEmploymentDetails', 'getApplication'],
    }),

    // ── Dynamic Liability Mutations ───────────────────────────────
    createDynamicLiability: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/liabilities`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getLiabilitiesDetails', 'getApplication'],
    }),
    updateDynamicLiability: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/liabilities`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getLiabilitiesDetails', 'getApplication'],
    }),
    deleteDynamicLiability: builder.mutation({
      query: ({ userLiabilityId, userId, applicationId }) => {
        const params = {};
        if (userId) params.user_id = userId;
        if (applicationId) params.applicationId = applicationId;
        return {
          url: `b2c/loan/dashboard/liabilities/${userLiabilityId}`,
          method: 'DELETE',
          params: Object.keys(params).length ? params : undefined,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getLiabilitiesDetails', 'getApplication'],
    }),

    // ── Real Estate Dynamic Mutations ─────────────────────────────
    createRealEstateDetailsV1: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/reos`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getRealEstateDetails', 'getApplication'],
    }),

    deleteRealEstateById: builder.mutation({
      query: ({ reosId, userId, applicationId }) => {
        const params = {};
        if (userId) params.user_id = userId;
        if (applicationId) params.applicationId = applicationId;
        return {
          url: `b2c/loan/dashboard/reos/${reosId}`,
          method: 'DELETE',
          params: Object.keys(params).length ? params : undefined,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getRealEstateDetails', 'getApplication'],
    }),

    updateRealEstateDetailsV1: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/reos`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getRealEstateDetails', 'getApplication'],
    }),

    // ── Co-Borrower Section Mutations ─────────────────────────────
    createCoBorrowerEmployment: builder.mutation({
      query: ({ data }) => ({
        url: `b2c/loan/co_borrower/employment_and_income`,
        method: 'POST',
        body: data,
        headers: getHeaders(),
      }),
      invalidatesTags: ['getCoBorrowerEmploymentDetails', 'getEmploymentDetails', 'getApplication'],
    }),
    updateCoBorrowerEmployment: builder.mutation({
      query: ({ data }) => ({
        url: `b2c/loan/co_borrower/employment_and_income`,
        method: 'PUT',
        body: data,
        headers: getHeaders(),
      }),
      invalidatesTags: ['getCoBorrowerEmploymentDetails', 'getEmploymentDetails', 'getApplication'],
    }),
    deleteCoBorrowerEmployment: builder.mutation({
      query: ({ employmentIncomeId, applicationId }) => {
        const params = {};
        if (applicationId) params.applicationId = applicationId;
        return {
          url: `b2c/loan/co_borrower/employment_and_income/${employmentIncomeId}`,
          method: 'DELETE',
          params: Object.keys(params).length ? params : undefined,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getCoBorrowerEmploymentDetails', 'getEmploymentDetails', 'getApplication'],
    }),
    createCoBorrowerAssets: builder.mutation({
      query: ({ data }) => ({
        url: `b2c/loan/co_borrower/assets`,
        method: 'POST',
        body: data,
        headers: getHeaders(),
      }),
      invalidatesTags: ['getCoBorrowerAssetDetails', 'getAssetDetails', 'getApplication'],
    }),
    updateCoBorrowerAssets: builder.mutation({
      query: ({ data }) => ({
        url: `b2c/loan/co_borrower/assets`,
        method: 'PUT',
        body: data,
        headers: getHeaders(),
      }),
      invalidatesTags: ['getCoBorrowerAssetDetails', 'getAssetDetails', 'getApplication'],
    }),
    deleteCoBorrowerAssets: builder.mutation({
      query: ({ assetId, applicationId }) => {
        const params = {};
        if (applicationId) params.applicationId = applicationId;
        return {
          url: `b2c/loan/co_borrower/assets/${assetId}`,
          method: 'DELETE',
          params: Object.keys(params).length ? params : undefined,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getCoBorrowerAssetDetails', 'getAssetDetails', 'getApplication'],
    }),
    createCoBorrowerLiabilities: builder.mutation({
      query: ({ data }) => ({
        url: `b2c/loan/co_borrower/liabilities`,
        method: 'POST',
        body: data,
        headers: getHeaders(),
      }),
      invalidatesTags: ['getCoBorrowerLiabilitiesDetails', 'getLiabilitiesDetails', 'getApplication'],
    }),
    updateCoBorrowerLiabilities: builder.mutation({
      query: ({ data }) => ({
        url: `b2c/loan/co_borrower/liabilities`,
        method: 'PUT',
        body: data,
        headers: getHeaders(),
      }),
      invalidatesTags: ['getCoBorrowerLiabilitiesDetails', 'getLiabilitiesDetails', 'getApplication'],
    }),
    deleteCoBorrowerLiabilities: builder.mutation({
      query: ({ liabilityId, applicationId }) => {
        const params = {};
        if (applicationId) params.applicationId = applicationId;
        return {
          url: `b2c/loan/co_borrower/liabilities/${liabilityId}`,
          method: 'DELETE',
          params: Object.keys(params).length ? params : undefined,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getCoBorrowerLiabilitiesDetails', 'getLiabilitiesDetails', 'getApplication'],
    }),
    createCoBorrowerReos: builder.mutation({
      query: ({ data }) => ({
        url: `b2c/loan/co_borrower/reos`,
        method: 'POST',
        body: data,
        headers: getHeaders(),
      }),
      invalidatesTags: ['getCoBorrowerRealEstateDetails', 'getRealEstateDetails', 'getApplication'],
    }),
    updateCoBorrowerReos: builder.mutation({
      query: ({ data }) => ({
        url: `b2c/loan/co_borrower/reos`,
        method: 'PUT',
        body: data,
        headers: getHeaders(),
      }),
      invalidatesTags: ['getCoBorrowerRealEstateDetails', 'getRealEstateDetails', 'getApplication'],
    }),
    deleteCoBorrowerReos: builder.mutation({
      query: ({ reoId, applicationId }) => {
        const params = {};
        if (applicationId) params.applicationId = applicationId;
        return {
          url: `b2c/loan/co_borrower/reos/${reoId}`,
          method: 'DELETE',
          params: Object.keys(params).length ? params : undefined,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getCoBorrowerRealEstateDetails', 'getRealEstateDetails', 'getApplication'],
    }),
  }),
});

export const {
  // Legacy heavy GET
  useGetBorrowerProfileQuery,
  // Per-section lightweight GETs
  useGetPersonalDetailsQuery,
  useGetLoanDetailsQuery,
  useGetEmploymentDetailsQuery,
  useGetAssetDetailsQuery,
  useGetLiabilitiesDetailsQuery,
  useGetRealEstateDetailsQuery,
  useGetDeclarationsDetailsQuery,
  useGetDemographicsDetailsQuery,
  // Co-borrower section GETs
  useGetCoBorrowerPersonalDetailsQuery,
  useGetCoBorrowerEmploymentDetailsQuery,
  useGetCoBorrowerAssetDetailsQuery,
  useGetCoBorrowerLiabilitiesDetailsQuery,
  useGetCoBorrowerRealEstateDetailsQuery,
  // Primary borrower write mutations
  useCreatePersonalDetailsMutation,
  useUpdatePersonalDetailsMutation,
  useCreateLoanDetailsMutation,
  useUpdateLoanDetailsMutation,
  useCreateEmploymentDetailsMutation,
  useUpdateEmploymentDetailsMutation,
  useCreateAssetDetailsMutation,
  useUpdateAssetDetailsMutation,
  useDeleteAssetDetailsMutation,
  useCreateLiabilitiesDetailsMutation,
  useUpdateLiabilitiesDetailsMutation,
  useCreateRealEstateDetailsMutation,
  useUpdateRealEstateDetailsMutation,
  useCreateDeclarationDetailsMutation,
  useUpdateDeclarationDetailsMutation,
  useCreateDemographicsDetailsMutation,
  useUpdateDemographicsDetailsMutation,
  // Dropdown queries
  // useGetApplicationQuery — removed, use useGetBorrowerProfileQuery instead
  useGetOwnerShipQuery,
  useLazyGetOwnerShipQuery,
  useGetLoanPurposeQuery,
  useLazyGetLoanPurposeQuery,
  useGetLoanTypeQuery,
  useLazyGetLoanTypeQuery,
  useGetOccupancyQuery,
  useLazyGetOccupancyQuery,
  useGetLoanProgramQuery,
  useLazyGetLoanProgramQuery,
  useGetPropertyTypeQuery,
  useLazyGetPropertyTypeQuery,
  useGetAccountTypeQuery,
  useGetLiabilityTypeQuery,
  useLazyGetLiabilityTypeQuery,
  useGetRefinancePurposeQuery,
  useLazyGetRefinancePurposeQuery,
  useGetEthnicityQuery,
  useLazyGetEthnicityQuery,
  useGetUserRaceQuery,
  useLazyGetUserRaceQuery,
  useGetEmploymentTypeQuery,
  useLazyGetEmploymentTypeQuery,
  useGetMartialStatusQuery,
  useLazyGetMartialStatusQuery,
  useGetCitizenshipStatusQuery,
  useLazyGetCitizenshipStatusQuery,
  useGetAssetTypeQuery,
  useLazyGetAssetTypeQuery,
  useGetLoanTermYearsQuery,
  useLazyGetLoanTermYearsQuery,
  useGetIncomeTypeQuery,
  useLazyGetIncomeTypeQuery,
  useGetMannerHeldQuery,
  useLazyGetMannerHeldQuery,
  useGetPropertyRightQuery,
  useLazyGetPropertyRightQuery,
  useGetApplicationStatusQuery,
  useLazyGetApplicationStatusQuery,
  useGetStatesQuery,
  useLazyGetStatesQuery,
  useGetCountiesByStateQuery,
  useGetDropdownBundleQuery,
  useGetLoanInfoDropdownBundleQuery,
  // useGetDynamicApplicationQuery — removed, use useGetBorrowerProfileQuery instead
  // Spouse / co-borrower personal details
  useCreateSpousePersonalDetailsMutation,
  useUpdateSpousePersonalDetailsMutation,
  useAddSpouseCoBorrowerMutation,
  useInviteCoBorrowerMutation,
  useGetLoanCoborrowersQuery,
  useRemoveCoborrowerMutation,
  // Dynamic employment
  useCreateDynamicEmploymentDetailsMutation,
  useUpdateDynamicEmploymentDetailsMutation,
  useDeleteDynamicEmploymentDetailsMutation,
  // Dynamic liabilities
  useCreateDynamicLiabilityMutation,
  useUpdateDynamicLiabilityMutation,
  useDeleteDynamicLiabilityMutation,
  // Dynamic real estate
  useCreateRealEstateDetailsV1Mutation,
  useUpdateRealEstateDetailsV1Mutation,
  useDeleteRealEstateByIdMutation,
  // Co-borrower section mutations
  useCreateCoBorrowerEmploymentMutation,
  useUpdateCoBorrowerEmploymentMutation,
  useDeleteCoBorrowerEmploymentMutation,
  useCreateCoBorrowerAssetsMutation,
  useUpdateCoBorrowerAssetsMutation,
  useDeleteCoBorrowerAssetsMutation,
  useCreateCoBorrowerLiabilitiesMutation,
  useUpdateCoBorrowerLiabilitiesMutation,
  useDeleteCoBorrowerLiabilitiesMutation,
  useCreateCoBorrowerReosMutation,
  useUpdateCoBorrowerReosMutation,
  useDeleteCoBorrowerReosMutation,
} = applicationAPI;
